/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Flag } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ReportDialog = ({ targetType, targetId, trigger }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/moderation/report', {
        target_type: targetType,
        target_id: targetId,
        reason: reason.trim(),
      });
      toast.success(t('reportSent'));
      setOpen(false);
      setReason('');
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" data-testid="report-open-button">
            <Flag className="h-4 w-4" /> {t('report')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader><DialogTitle>{t('report')}</DialogTitle></DialogHeader>
        <div>
          <Label className="text-xs text-muted-foreground">{t('reportReason')}</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !reason.trim()} data-testid="report-submit-button">
            {submitting ? t('loading') : t('report')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
